// Setup → AI management → Credits & limits: the GET and PUT /api/ai/limits calls
// (apps/server/src/aiLimits.mjs), their mapping onto @olytic/foundation/ai-admin's
// UsageCapCard and UsageCapDialog, and the warning the three AI management pages
// open with when usage nears or passes the cap. Free of React, so it is unit
// tested (aiLimitsApi.test.ts); useAiLimits.ts is the hook.
import { formatTokens } from './aiUsageApi';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export const LIMITS_PATH = '/api/ai/limits';

/** Where usage stands against the cap: none (no cap), ok (under 80%), near (80% to under 100%), over (100% or more). */
export type LimitsStatus = 'none' | 'ok' | 'near' | 'over';

/** The body of GET and PUT /api/ai/limits. Amounts are tokens (input plus output), this UTC calendar month. */
export interface LimitsResponse {
  /** YYYY-MM-DD: the month's first day, and the first day of the next. */
  period: { start: string; end: string };
  used: number;
  runs: number;
  cap: number | null;
  percentUsed: number | null;
  status: LimitsStatus;
  updatedAt: string | null;
}

/** The limits as a page holds them: loading, failed (no warning), or loaded. */
export type AiLimitsLoad =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'ready'; body: LimitsResponse };

/** The Credits & limits page's note, in place of the old placeholders banner. */
export const LIMITS_NOTE = 'Usage is counted in tokens. The cap only warns: nothing is switched off when it is reached.';

/** What the Credits & limits page hands to UsageCapCard (its `periodEnd` reads "Resets <first of next month>"). */
export function capCardProps(body: LimitsResponse): { used: number; cap: number | null; periodEnd: string } {
  return { used: body.used, cap: body.cap, periodEnd: body.period.end };
}

/**
 * The warning the three AI management pages open with, or null: none under 80%,
 * with no cap, or without the figures (a failed load warns of nothing).
 */
export function limitsWarning(body: LimitsResponse | null): string | null {
  if (!body || body.cap === null) return null;
  const cap = formatTokens(body.cap);
  if (body.status === 'near') return `AI usage is at ${body.percentUsed ?? 0}% of this month's cap of ${cap} tokens.`;
  if (body.status === 'over') return `AI usage is over this month's cap of ${cap} tokens. Nothing is blocked.`;
  return null;
}

/** The server's message from a failed response, or `fallback`. */
async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const payload = (await response.json()) as { error?: unknown };
    if (typeof payload.error === 'string') return payload.error;
  } catch {
    // Not JSON.
  }
  return fallback;
}

/** GET /api/ai/limits; an error carries the server's message when it sends one. */
export async function fetchAiLimits(fetcher: Fetch, init?: RequestInit): Promise<LimitsResponse> {
  const response = await fetcher(LIMITS_PATH, init);
  if (!response.ok) throw new Error(await errorMessage(response, 'Could not load AI limits.'));
  return (await response.json()) as LimitsResponse;
}

/**
 * PUT /api/ai/limits, setting the cap or clearing it (null). Resolves to the
 * GET's body as saved; an error carries the server's message, or "Try again."
 */
export async function saveAiCap(fetcher: Fetch, monthlyTokenCap: number | null): Promise<LimitsResponse> {
  const response = await fetcher(LIMITS_PATH, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ monthlyTokenCap }),
  });
  if (!response.ok) throw new Error(await errorMessage(response, 'Try again.'));
  return (await response.json()) as LimitsResponse;
}
